import * as z from "zod";

export const ANONYMOUS_PLAYER_ID_STORAGE_KEY = "aw_player_id";
const anonymousPlayerIdSchema = z.uuid();

export function isValidAnonymousPlayerId(playerId: string): boolean {
  return anonymousPlayerIdSchema.safeParse(playerId.trim()).success;
}

function clearLegacyAnonymousPlayerIdStorage(): void {
  try {
    window.localStorage?.removeItem(ANONYMOUS_PLAYER_ID_STORAGE_KEY);
  } catch {}
}

export function readAnonymousPlayerId(): string | undefined {
  if (typeof window === "undefined") return undefined;
  clearLegacyAnonymousPlayerIdStorage();
  try {
    const value = window.sessionStorage?.getItem(ANONYMOUS_PLAYER_ID_STORAGE_KEY);
    return value && value.length > 0 ? value : undefined;
  } catch {
    return undefined;
  }
}

function writeAnonymousPlayerIdClient(playerId: string): void {
  clearLegacyAnonymousPlayerIdStorage();
  window.sessionStorage?.setItem(ANONYMOUS_PLAYER_ID_STORAGE_KEY, playerId);
  document.cookie = `${ANONYMOUS_PLAYER_ID_STORAGE_KEY}=${encodeURIComponent(playerId)}; path=/; samesite=lax`;
}

export function clearAnonymousPlayerIdClient(): void {
  clearLegacyAnonymousPlayerIdStorage();
  window.sessionStorage?.removeItem(ANONYMOUS_PLAYER_ID_STORAGE_KEY);
  document.cookie = `${ANONYMOUS_PLAYER_ID_STORAGE_KEY}=; path=/; max-age=0; samesite=lax`;
}

export function persistAnonymousPlayerIdFromSession({
  playerId,
  isAnonymous,
}: {
  playerId: string;
  isAnonymous: boolean;
}): void {
  if (typeof window === "undefined") return;
  if (isAnonymous) {
    writeAnonymousPlayerIdClient(playerId);
    return;
  }
  clearAnonymousPlayerIdClient();
}

export function syncAnonymousPlayerIdOnClient({
  session,
}: {
  session: { playerId: string; isAnonymous: boolean };
}): { shouldInvalidate: boolean } {
  if (typeof window === "undefined") {
    return { shouldInvalidate: false };
  }

  if (!session.isAnonymous) {
    clearAnonymousPlayerIdClient();
    return { shouldInvalidate: false };
  }

  const clientPlayerId = readAnonymousPlayerId();
  if (clientPlayerId && isValidAnonymousPlayerId(clientPlayerId)) {
    persistAnonymousPlayerIdFromSession({ playerId: clientPlayerId, isAnonymous: true });
    return { shouldInvalidate: clientPlayerId !== session.playerId };
  }

  persistAnonymousPlayerIdFromSession(session);
  return { shouldInvalidate: clientPlayerId !== undefined };
}
